import React, { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle,
  CheckCircle2,
  Edit2,
  EyeOff,
  Eye,
  Merge,
  Shield,
  ShieldAlert,
  Landmark,
  Calendar,
  Hash,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "wouter";

export default function AccountsManagement() {
  const { language, t } = useLanguage();
  const [, navigate] = useLocation();
  const isRTL = language === "ar";

  // Data queries
  const { data: accounts, isLoading, refetch: refetchAccounts } = trpc.bankAccount.listWithStats.useQuery();
  const { data: integrityData, isLoading: integrityLoading, refetch: refetchIntegrity } = trpc.bankAccount.integrityCheck.useQuery();

  // Mutations
  const updateAccount = trpc.bankAccount.update.useMutation({
    onSuccess: () => {
      refetchAccounts();
      toast.success(isRTL ? "تم تحديث الحساب" : "Account updated");
    },
  });
  const mergeAccounts = trpc.bankAccount.merge.useMutation({
    onSuccess: (data) => {
      refetchAccounts();
      refetchIntegrity();
      setMergeDialogOpen(false);
      toast.success(isRTL ? `تم دمج ${data.reassignedCount} معاملة` : `Merged ${data.reassignedCount} transactions`);
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  // UI state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState<number | null>(null);
  const [mergeTarget, setMergeTarget] = useState<number | null>(null);

  // Helpers
  const getChainStatus = (accountId: number) => {
    if (!integrityData) return null;
    const issue = integrityData.issues.find((i) => i.bankAccountId === accountId);
    if (!issue || issue.breakCount === 0) return "clean";
    return "broken";
  };

  const getChainBreaks = (accountId: number) => {
    if (!integrityData) return [];
    const issue = integrityData.issues.find((i) => i.bankAccountId === accountId);
    return issue?.breaks ?? [];
  };

  const formatDate = (ts: number | null) => {
    if (!ts) return "—";
    return new Date(ts).toLocaleDateString(isRTL ? "ar-KW" : "en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  const handleStartEdit = (id: number, currentLabel: string | null, accountKey: string) => {
    setEditingId(id);
    setEditLabel(currentLabel ?? accountKey);
  };

  const handleSaveEdit = (id: number) => {
    updateAccount.mutate({ id, accountLabel: editLabel || null });
    setEditingId(null);
  };

  const handleToggleActive = (id: number, currentActive: number) => {
    updateAccount.mutate({ id, isActive: currentActive === 1 ? 0 : 1 });
  };

  const handleMerge = () => {
    if (mergeSource && mergeTarget) {
      mergeAccounts.mutate({ sourceAccountId: mergeSource, targetAccountId: mergeTarget });
    }
  };

  const activeAccounts = accounts?.filter((a) => a.isActive === 1) ?? [];
  const inactiveAccounts = accounts?.filter((a) => a.isActive === 0) ?? [];

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-4xl mx-auto px-2 sm:px-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 max-w-4xl mx-auto px-2 sm:px-4" dir={isRTL ? "rtl" : "ltr"}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold text-foreground flex items-center gap-2">
            <Landmark className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            {isRTL ? "إدارة الحسابات" : "Bank Accounts"}
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
            {isRTL ? "عرض وإدارة حساباتك البنكية المسجلة" : "View and manage your registered bank accounts"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { refetchIntegrity(); toast.success(isRTL ? "تم تحديث حالة السلسلة" : "Chain status refreshed"); }}
            disabled={integrityLoading}
            className="gap-1.5"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${integrityLoading ? "animate-spin" : ""}`} />
            <span className="text-xs">{isRTL ? "فحص السلامة" : "Check Integrity"}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMergeDialogOpen(true)}
            disabled={activeAccounts.length < 2}
            className="gap-1.5"
          >
            <Merge className="h-3.5 w-3.5" />
            <span className="text-xs">{isRTL ? "دمج حسابات" : "Merge Accounts"}</span>
          </Button>
        </div>
      </div>

      {/* Unverified Accounts Warning */}
      {accounts?.some((a) => a.isUnverified === 1 && a.isActive === 1) && (
        <div className="rounded-xl border border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20 p-3 sm:p-4">
          <div className="flex items-start gap-2.5">
            <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                {isRTL ? "حسابات تحتاج تأكيد" : "Accounts Need Confirmation"}
              </p>
              <p className="text-xs text-amber-700/80 dark:text-amber-400/70 mt-1">
                {isRTL
                  ? "ميزانو لم يتمكن من تأكيد رقم الحساب لبعض الحسابات أدناه. قد تكون معاملات من حسابات مختلفة مدمجة معاً. يرجى مراجعة الحسابات المميزة وإعادة تسميتها أو دمجها أو إعادة رفع كشوفاتها."
                  : "Mizano could not confirm the account number for some accounts below. Transactions from different accounts may be merged together. Please review the flagged accounts and rename, merge, or re-upload their statements."}
              </p>
            </div>
          </div>
        </div>
      )}

      <Button type="button" variant="link" className="min-h-11 w-fit px-0 text-sm" onClick={() => navigate("/account?tab=balance")}>
        {t("accounts.balanceStatusLink")}
      </Button>

      {/* Active Accounts */}
      <div className="space-y-3">
        {activeAccounts.map((account) => {
          const chainStatus = getChainStatus(account.id);
          const breaks = getChainBreaks(account.id);
          const isEditing = editingId === account.id;

          return (
            <Card
              key={account.id}
              className={`transition-all ${
                account.isUnverified === 1
                  ? "border-amber-300 dark:border-amber-700 border-dashed"
                  : "border-border"
              }`}
            >
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col gap-3">
                  {/* Top row: bank info + actions */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            className="h-8 text-sm max-w-[200px]"
                            autoFocus
                            onKeyDown={(e) => { if (e.key === "Enter") handleSaveEdit(account.id); if (e.key === "Escape") setEditingId(null); }}
                          />
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => handleSaveEdit(account.id)}>
                            {isRTL ? "حفظ" : "Save"}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-muted-foreground" onClick={() => setEditingId(null)}>
                            {isRTL ? "إلغاء" : "Cancel"}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm sm:text-base font-semibold text-foreground truncate">
                            {account.accountLabel ?? account.accountKey}
                          </h3>
                          <button
                            onClick={() => handleStartEdit(account.id, account.accountLabel, account.accountKey)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="secondary" className="text-[10px] sm:text-xs">
                          {account.bankName}
                        </Badge>
                        {account.accountNumberMasked && (
                          <span className="text-[10px] sm:text-xs text-muted-foreground font-mono">
                            ****{account.accountNumberMasked}
                          </span>
                        )}
                        <span className="text-[10px] sm:text-xs text-muted-foreground">
                          {account.currency}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {/* Chain status badge */}
                      {chainStatus === "clean" && (
                        <Badge variant="outline" className="gap-1 text-[10px] border-green-300 dark:border-green-700 text-green-700 dark:text-green-400">
                          <CheckCircle2 className="h-3 w-3" />
                          <span className="hidden sm:inline">{isRTL ? "سليم" : "Clean"}</span>
                        </Badge>
                      )}
                      {chainStatus === "broken" && (
                        <Badge variant="outline" className="gap-1 text-[10px] border-red-300 dark:border-red-700 text-red-700 dark:text-red-400">
                          <AlertTriangle className="h-3 w-3" />
                          <span className="hidden sm:inline">{isRTL ? `${breaks.length} فجوة` : `${breaks.length} breaks`}</span>
                        </Badge>
                      )}
                      {account.isUnverified === 1 && (
                        <Badge variant="outline" className="gap-1 text-[10px] border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400">
                          <ShieldAlert className="h-3 w-3" />
                          <span className="hidden sm:inline">{isRTL ? "غير مؤكد" : "Unverified"}</span>
                        </Badge>
                      )}
                      {/* Toggle active */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => handleToggleActive(account.id, account.isActive)}
                        title={isRTL ? "إخفاء الحساب" : "Hide account"}
                      >
                        <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-2 sm:gap-4">
                    <div className="flex items-center gap-1.5">
                      <Hash className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      <div>
                        <p className="text-[10px] text-muted-foreground">{isRTL ? "المعاملات" : "Transactions"}</p>
                        <p className="text-xs sm:text-sm font-medium">{account.transactionCount}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      <div>
                        <p className="text-[10px] text-muted-foreground">{isRTL ? "من" : "From"}</p>
                        <p className="text-xs sm:text-sm font-medium">{formatDate(account.firstTransactionDate)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      <div>
                        <p className="text-[10px] text-muted-foreground">{isRTL ? "إلى" : "To"}</p>
                        <p className="text-xs sm:text-sm font-medium">{formatDate(account.lastTransactionDate)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Unverified prompt */}
                  {account.isUnverified === 1 && (
                    <div className="rounded-lg bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-2.5 sm:p-3">
                      <p className="text-xs text-amber-800 dark:text-amber-300">
                        {isRTL
                          ? "⚠️ لم يتمكن ميزانو من تأكيد رقم هذا الحساب. قد تكون المعاملات المسجلة هنا تخص حساباً مختلفاً. يرجى التأكد من صحة التسمية أو إعادة رفع كشف الحساب."
                          : "⚠️ Mizano could not confirm this account number. The transactions recorded here may belong to a different account. Please verify the label is correct or re-upload the statement."}
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1"
                          onClick={() => handleStartEdit(account.id, account.accountLabel, account.accountKey)}
                        >
                          <Edit2 className="h-3 w-3" />
                          {isRTL ? "إعادة تسمية" : "Rename"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1"
                          onClick={() => {
                            updateAccount.mutate({ id: account.id, accountLabel: account.accountLabel });
                            toast.success(isRTL ? "تم تأكيد الحساب" : "Account confirmed");
                          }}
                        >
                          <Shield className="h-3 w-3" />
                          {isRTL ? "تأكيد" : "Confirm"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Inactive Accounts */}
      {inactiveAccounts.length > 0 && (
        <>
          <Separator />
          <div>
            <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <EyeOff className="h-4 w-4" />
              {isRTL ? "حسابات مخفية" : "Hidden Accounts"} ({inactiveAccounts.length})
            </h2>
            <div className="space-y-2">
              {inactiveAccounts.map((account) => (
                <div
                  key={account.id}
                  className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg border border-dashed border-muted bg-muted/30"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-muted-foreground truncate">
                      {account.accountLabel ?? account.accountKey}
                    </span>
                    <Badge variant="secondary" className="text-[10px]">{account.bankName}</Badge>
                    <span className="text-[10px] text-muted-foreground">{account.transactionCount} txn</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={() => handleToggleActive(account.id, account.isActive)}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    {isRTL ? "إظهار" : "Show"}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Merge Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="h-5 w-5 text-primary" />
              {isRTL ? "دمج حسابين" : "Merge Two Accounts"}
            </DialogTitle>
            <DialogDescription>
              {isRTL
                ? "سيتم نقل جميع المعاملات من الحساب المصدر إلى الحساب الهدف، وإخفاء الحساب المصدر. هذا الإجراء لا يمكن التراجع عنه."
                : "All transactions from the source account will be moved to the target account, and the source will be hidden. This action cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                {isRTL ? "الحساب المصدر (سيتم إخفاؤه)" : "Source Account (will be hidden)"}
              </label>
              <Select value={mergeSource?.toString() ?? ""} onValueChange={(v) => setMergeSource(parseInt(v))}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={isRTL ? "اختر الحساب المصدر" : "Select source account"} />
                </SelectTrigger>
                <SelectContent>
                  {activeAccounts.filter((a) => a.id !== mergeTarget).map((a) => (
                    <SelectItem key={a.id} value={a.id.toString()}>
                      {a.accountLabel ?? a.accountKey} ({a.transactionCount} txn)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                {isRTL ? "الحساب الهدف (سيحتفظ بالمعاملات)" : "Target Account (will keep transactions)"}
              </label>
              <Select value={mergeTarget?.toString() ?? ""} onValueChange={(v) => setMergeTarget(parseInt(v))}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={isRTL ? "اختر الحساب الهدف" : "Select target account"} />
                </SelectTrigger>
                <SelectContent>
                  {activeAccounts.filter((a) => a.id !== mergeSource).map((a) => (
                    <SelectItem key={a.id} value={a.id.toString()}>
                      {a.accountLabel ?? a.accountKey} ({a.transactionCount} txn)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>
              {isRTL ? "إلغاء" : "Cancel"}
            </Button>
            <Button
              onClick={handleMerge}
              disabled={!mergeSource || !mergeTarget || mergeAccounts.isPending}
              className="gap-1.5"
            >
              <Merge className="h-4 w-4" />
              {mergeAccounts.isPending ? (isRTL ? "جاري الدمج..." : "Merging...") : (isRTL ? "دمج" : "Merge")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
